import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../Components/Navigation';
import Footer from '../Components/Footer';

const Section = ({ title, children }) => (
    <div className="mb-10">
        <h2 className="text-xl font-bold text-midnight mb-4">{title}</h2>
        <div className="text-gray-600 leading-relaxed space-y-3">{children}</div>
    </div>
);

const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans antialiased text-gray-900 bg-white">
            <Navigation solid />

            {/* Header */}
            <div className="bg-midnight pt-36 pb-16">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <span className="inline-block bg-tealVitality/20 text-tealVitality text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                        Transparência &amp; Privacidade
                    </span>
                    <h1 className="text-4xl font-extrabold text-white font-display mb-4">
                        Política de Privacidade
                    </h1>
                    <p className="text-gray-400 text-base">
                        Última atualização: 20 de março de 2026
                    </p>
                </div>
            </div>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">

                <Section title="1. Quem somos">
                    <p>
                        A <strong>PawSuite Technologies Ltda.</strong> ("PawSuite", "nós" ou "nosso"), inscrita no CNPJ sob o nº 00.000.000/0001-00,
                        com sede na Cidade de São Paulo – SP, é a controladora dos dados pessoais tratados por meio da plataforma PawSuite,
                        disponível em <strong>pawsuite.com.br</strong> e em aplicativos móveis associados.
                    </p>
                    <p>
                        Esta Política de Privacidade descreve como coletamos, utilizamos, armazenamos e protegemos as informações pessoais
                        dos nossos usuários, em conformidade com a <strong>Lei Geral de Proteção de Dados Pessoais (LGPD – Lei nº 13.709/2018)</strong>
                        e demais normas aplicáveis.
                    </p>
                </Section>

                <Section title="2. Dados que coletamos">
                    <p>Coletamos as seguintes categorias de dados pessoais:</p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>
                            <strong>Dados de cadastro:</strong> nome completo, CPF/CNPJ, endereço de e-mail, telefone,
                            endereço do estabelecimento e dados do responsável pelo pet shop.
                        </li>
                        <li>
                            <strong>Dados de acesso:</strong> credenciais de login (e-mail e senha criptografada),
                            endereço IP, tipo de dispositivo, sistema operacional e navegador.
                        </li>
                        <li>
                            <strong>Dados financeiros:</strong> informações de pagamento de plano (processadas por
                            gateway certificado PCI-DSS – não armazenamos dados completos de cartão).
                        </li>
                        <li>
                            <strong>Dados operacionais:</strong> registros de agendamentos, prontuários de pets,
                            histórico de serviços, notificações enviadas aos tutores e informações inseridas pelos
                            usuários no uso da plataforma.
                        </li>
                        <li>
                            <strong>Dados dos tutores e pets:</strong> nome do tutor, contato e informações sobre
                            os animais de estimação (nome, espécie, raça, peso, histórico de saúde), inseridos pelos
                            próprios pet shops no uso da plataforma.
                        </li>
                        <li>
                            <strong>Dados de uso:</strong> logs de acesso, páginas visitadas, funcionalidades
                            utilizadas e duração das sessões, para fins de melhoria do serviço.
                        </li>
                    </ul>
                </Section>

                <Section title="3. Finalidade e base legal do tratamento">
                    <p>Tratamos seus dados pessoais com as seguintes finalidades e bases legais:</p>
                    <div className="overflow-x-auto mt-4">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="text-left px-4 py-3 border border-gray-200 font-semibold text-midnight">Finalidade</th>
                                    <th className="text-left px-4 py-3 border border-gray-200 font-semibold text-midnight">Base Legal (LGPD)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ["Prestação dos serviços contratados", "Execução de contrato (Art. 7º, V)"],
                                    ["Processamento de pagamentos e faturamento", "Execução de contrato (Art. 7º, V)"],
                                    ["Comunicações sobre a conta e suporte técnico", "Execução de contrato (Art. 7º, V)"],
                                    ["Envio de comunicações de marketing e novidades", "Consentimento (Art. 7º, I) — revogável a qualquer momento"],
                                    ["Prevenção a fraudes e segurança da plataforma", "Legítimo interesse (Art. 7º, IX)"],
                                    ["Cumprimento de obrigações legais e fiscais", "Obrigação legal (Art. 7º, II)"],
                                    ["Análise e melhoria da plataforma", "Legítimo interesse (Art. 7º, IX)"],
                                ].map(([fin, base], i) => (
                                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                                        <td className="px-4 py-3 border border-gray-200">{fin}</td>
                                        <td className="px-4 py-3 border border-gray-200">{base}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>

                <Section title="4. Compartilhamento de dados">
                    <p>
                        Não vendemos nem alugamos seus dados pessoais a terceiros. Podemos compartilhá-los apenas nas
                        seguintes situações:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>
                            <strong>Fornecedores e parceiros de serviço</strong> (processadores de pagamento, provedores de
                            infraestrutura em nuvem, ferramentas de suporte) — vinculados a acordos de confidencialidade
                            e com tratamento restrito às finalidades autorizadas.
                        </li>
                        <li>
                            <strong>Autoridades públicas</strong> — quando exigido por ordem judicial, obrigação legal ou
                            regulatória.
                        </li>
                        <li>
                            <strong>Operações societárias</strong> — em caso de fusão, aquisição ou reestruturação,
                            garantindo que os dados sigam protegidos por política equivalente.
                        </li>
                    </ul>
                    <p className="mt-3">
                        Todos os fornecedores são selecionados com base em critérios de segurança e privacidade, e estão
                        contratualmente obrigados a proteger suas informações.
                    </p>
                </Section>

                <Section title="5. Armazenamento, segurança e retenção">
                    <p>
                        Seus dados são armazenados em servidores seguros localizados no Brasil ou em países que oferecem
                        nível adequado de proteção, conforme determinação da ANPD. Adotamos medidas técnicas e
                        organizacionais para proteger suas informações, incluindo:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>Criptografia em trânsito (TLS/HTTPS) e em repouso (AES-256).</li>
                        <li>Controle de acesso baseado em perfis e princípio do menor privilégio.</li>
                        <li>Monitoramento contínuo de segurança e testes periódicos de vulnerabilidade.</li>
                        <li>Backups regulares com proteção contra perda acidental.</li>
                    </ul>
                    <p className="mt-3">
                        Mantemos seus dados pelo período necessário para a prestação do serviço e pelo prazo mínimo
                        exigido pela legislação aplicável (ex.: dados fiscais por 5 anos). Após o encerramento da conta,
                        os dados são anonimizados ou excluídos no prazo de <strong>90 dias</strong>, salvo obrigação
                        legal de retenção.
                    </p>
                </Section>

                <Section title="6. Cookies e tecnologias de rastreamento">
                    <p>
                        Utilizamos cookies e tecnologias similares para melhorar sua experiência na plataforma:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li><strong>Cookies essenciais:</strong> necessários para o funcionamento da plataforma (autenticação, segurança).</li>
                        <li><strong>Cookies analíticos:</strong> coletam dados anônimos de uso para aprimorar a experiência (ex.: Google Analytics).</li>
                        <li><strong>Cookies de preferências:</strong> memorizam suas configurações de uso.</li>
                    </ul>
                    <p className="mt-3">
                        Você pode gerenciar suas preferências de cookies nas configurações do navegador. A desativação de
                        cookies essenciais pode impactar o funcionamento de algumas funcionalidades.
                    </p>
                </Section>

                <Section title="7. Seus direitos como titular">
                    <p>
                        Nos termos da LGPD (Art. 18), você tem os seguintes direitos em relação aos seus dados pessoais:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li><strong>Confirmação e acesso:</strong> saber se tratamos seus dados e obter cópia deles.</li>
                        <li><strong>Correção:</strong> solicitar a correção de dados incompletos, inexatos ou desatualizados.</li>
                        <li><strong>Anonimização, bloqueio ou eliminação:</strong> de dados desnecessários ou tratados em desconformidade.</li>
                        <li><strong>Portabilidade:</strong> receber seus dados em formato estruturado e interoperável.</li>
                        <li><strong>Revogação do consentimento:</strong> retirar seu consentimento para tratamentos baseados nessa base legal.</li>
                        <li><strong>Oposição:</strong> se opor a tratamentos realizados com base em legítimo interesse.</li>
                        <li><strong>Eliminação dos dados:</strong> solicitar a exclusão dos dados pessoais tratados com base em consentimento.</li>
                        <li><strong>Informação sobre compartilhamento:</strong> saber com quem compartilhamos seus dados.</li>
                    </ul>
                    <p className="mt-3">
                        Para exercer seus direitos, entre em contato pelo e-mail{" "}
                        <a href="mailto:privacidade@pawsuite.com.br" className="text-tealVitality font-medium hover:underline">
                            privacidade@pawsuite.com.br
                        </a>
                        . Responderemos no prazo de até <strong>15 dias úteis</strong>.
                    </p>
                </Section>

                <Section title="8. Transferências internacionais de dados">
                    <p>
                        Alguns de nossos fornecedores de infraestrutura e serviços estão localizados fora do Brasil.
                        Nessas situações, garantimos que as transferências ocorram apenas para países ou organizações
                        com nível de proteção adequado reconhecido pela ANPD, ou mediante cláusulas contratuais
                        padrão que assegurem a proteção dos seus dados.
                    </p>
                </Section>

                <Section title="9. Encarregado de Proteção de Dados (DPO)">
                    <p>
                        Nossa Encarregada de Proteção de Dados (DPO) é responsável por monitorar o cumprimento
                        desta política e atender às suas solicitações:
                    </p>
                    <div className="mt-3 bg-gray-50 rounded-xl p-5 border border-gray-100">
                        <p><strong>Nome:</strong> DPO PawSuite</p>
                        <p className="mt-1">
                            <strong>E-mail:</strong>{" "}
                            <a href="mailto:dpo@pawsuite.com.br" className="text-tealVitality hover:underline">
                                dpo@pawsuite.com.br
                            </a>
                        </p>
                        <p className="mt-1"><strong>Endereço:</strong> São Paulo – SP, Brasil</p>
                    </div>
                </Section>

                <Section title="10. Alterações nesta Política">
                    <p>
                        Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças nos
                        nossos serviços, práticas ou exigências legais. Quando realizarmos alterações relevantes,
                        notificaremos você por e-mail ou por aviso em destaque na plataforma com, no mínimo,
                        <strong> 15 dias de antecedência</strong>.
                    </p>
                    <p>
                        O uso continuado da plataforma após a entrada em vigor das alterações constitui aceitação
                        da nova versão.
                    </p>
                </Section>

                {/* CTA */}
                <div className="mt-12 p-8 bg-accentWarm rounded-2xl border border-orange-100 text-center">
                    <p className="text-midnight font-semibold mb-2">Dúvidas sobre privacidade?</p>
                    <p className="text-gray-500 text-sm mb-4">
                        Entre em contato com nosso time. Estamos prontos para ajudar.
                    </p>
                    <a
                        href="mailto:privacidade@pawsuite.com.br"
                        className="inline-block bg-midnight text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-gray-800 transition-all"
                    >
                        privacidade@pawsuite.com.br
                    </a>
                </div>

                {/* Breadcrumb back */}
                <div className="mt-10 text-center">
                    <Link to="/" className="text-tealVitality text-sm font-medium hover:underline">
                        ← Voltar ao início
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
